"use client";

import { useEffect, useRef } from "react";

interface MeasurementProtocolProps {
  data: Record<string, any>;
  measurementId: string;
}

const MeasurementProtocol = ({ data, measurementId }: MeasurementProtocolProps) => {
  const sentRef = useRef(false);
  const lastDataRef = useRef<Record<string, any> | null>(null);
  const clientIdPromiseRef = useRef<Promise<string | null> | null>(null);
  const sessionIdPromiseRef = useRef<Promise<string | null> | null>(null);
  const clientIdRef = useRef<string | null>(null);
  const sessionIdRef = useRef<string | null>(null);


  // Helper functions for cookie parsing
  const getClientIdFromCookie = (): string | null => {
    if (typeof document !== "undefined") {
      const cookies = document.cookie.split(";");
      const gaCookie = cookies.find(cookie => 
        cookie.trim().startsWith("_ga=")
      );
      
      if (gaCookie) {
        const parts = gaCookie.split(".");
        if (parts.length >= 4) {
          return `${parts[2]}.${parts[3]}`;
        }
      }
    }
    return null;
  };

  const getSessionIdFromCookie = (): string | null => {
    if (typeof document !== "undefined") {
      const cookies = document.cookie.split(";");
      
      // GA4 session data is typically stored in _ga_<container_id> cookie
      // Format: GS1.1.session_id.timestamp
      const gaSessionCookie = cookies.find(cookie => 
        cookie.trim().startsWith("_ga_") && cookie.includes("GS1")
      );
      
      if (gaSessionCookie) {
        console.log("Found GA session cookie:", gaSessionCookie);
        const cookieValue = gaSessionCookie.split("=")[1];
        const parts = cookieValue.split(".");
        
        // Format: GS1.1.session_id.timestamp
        if (parts.length >= 4 && parts[0] === "GS1") {
          const sessionId = parts[2];
          console.log("Extracted session_id from cookie:", sessionId);
          return sessionId;
        }
      }
      
      console.log("No GA session cookie found");
    }
    return null;
  };

  // Function to ensure gtag is available
  const ensureGtagAvailable = (): boolean => {
    if (typeof window === "undefined") return false;
    
    // Check if gtag already exists
    if (window.gtag) {
      console.log("gtag already available globally");
      return true;
    }
    
    // Try to get gtag from dataLayer
    const {dataLayer} = (window as any);
    if (dataLayer && Array.isArray(dataLayer)) {
      console.log("Checking dataLayer for gtag setup...");
      
      // Create gtag function if it doesn't exist
      if (!window.gtag) {
        console.log("Creating gtag function from dataLayer...");
        (window as any).gtag = function gtag(...args: any[]) {
          (window as any).dataLayer.push(args);
        };
        
        // Verify it was created
        if (window.gtag) {
          console.log("gtag function created successfully");
          return true;
        }
      }
    }
    
    // Try to find gtag in GTM
    const gtm = (window as any).google_tag_manager;
    if (gtm) {
      console.log("Checking GTM for gtag...");
      // Sometimes GTM exposes gtag differently
      const gtagKeys = Object.keys(gtm).filter(key => key.includes("gtag") || key.includes("GA"));
      console.log("GTM keys with gtag/GA:", gtagKeys);
    }
    
    console.log("gtag not available and could not be initialized");
    return false;
  };

   // Function to wait for gtag to become available
  const waitForGtag = (maxWaitTime = 5000): Promise<boolean> => new Promise((resolve) => {
      if (ensureGtagAvailable()) {
        resolve(true);
        return;
      }
      
      console.log("Waiting for gtag to become available...");
      const startTime = Date.now();
      
      const checkGtag = () => {
        if (ensureGtagAvailable()) {
          console.log(`gtag became available after ${Date.now() - startTime}ms`);
          resolve(true);
          return;
        }
        
        if (Date.now() - startTime >= maxWaitTime) {
          console.log(`Timeout after ${maxWaitTime}ms - gtag not available`);
          resolve(false);
          return;
        }
        
        setTimeout(checkGtag, 100);
      };
      
      checkGtag();
    });

  

  const getGA4ClientId = (): Promise<string | null> => {
    // Return cached result if available
    if (clientIdRef.current !== null) {
      console.log("=== Using cached GA4 client id ===");
      return Promise.resolve(clientIdRef.current);
    }
    
    // Return existing promise if already in progress
    if (clientIdPromiseRef.current) {
      console.log("=== GA4 client id request already in progress ===");
      return clientIdPromiseRef.current;
    }
    
    console.log("=== Getting GA4 client id (new request) ===");
    
    const promise = new Promise<string | null>((resolve) => {
      // Debug what's available
      console.log("Debugging window objects:");
      console.log("- typeof window:", typeof window);
      console.log("- window.gtag exists:", typeof window !== "undefined" && !!window.gtag);
      console.log("- window.dataLayer exists:", typeof window !== "undefined" && !!(window as any).dataLayer);
      console.log("- window.google_tag_manager exists:", typeof window !== "undefined" && !!(window as any).google_tag_manager);
      
      if (typeof window !== "undefined") {
        // Check if gtag is available in different locations
        const globalGtag = (window as any).gtag;
        const gtagFromGTM = (window as any).google_tag_manager?.gtag;
        const gtagFromDataLayer = (window as any).dataLayer?.gtag;
        
        console.log("- window.gtag (direct):", globalGtag);
        console.log("- gtag from GTM:", gtagFromGTM);
        console.log("- gtag from dataLayer:", gtagFromDataLayer);
        
        // Try to get gtag function from any available source
        const gtag = globalGtag || gtagFromGTM || gtagFromDataLayer || window.gtag;
        
        console.log("Final gtag function selected:", typeof gtag);
        console.log("measurementId being used:", measurementId);
        
        if (gtag && typeof gtag === "function") {
          console.log("Found gtag function, attempting to get client_id...");
          try {
                        
            // Test if gtag is actually callable
            console.log("Testing gtag function...");
            
            // Add a timeout to prevent hanging if gtag callback never fires
            const timeoutId = setTimeout(() => {
              console.log("gtag callback timeout - falling back to cookies");
              const cookieClientId = getClientIdFromCookie();
              clientIdRef.current = cookieClientId;
              clientIdPromiseRef.current = null;
              resolve(cookieClientId);
            }, 3000); // 3 second timeout
            
            console.log("About to call gtag with:", "get", measurementId, "client_id");
            gtag("get", measurementId, "client_id", (clientId: string) => {
              console.log("=== GTAG CALLBACK FIRED ===");
              console.log("gtag callback received for client_id:", clientId, typeof clientId);
              clearTimeout(timeoutId); // Clear the timeout since callback fired
              
              if (clientId && clientId !== "undefined" && clientId.length > 0) {
                clientIdRef.current = clientId;
                clientIdPromiseRef.current = null;
                resolve(clientId);
              } else {
                console.log("Invalid client_id from gtag, falling back to cookies");
                const cookieClientId = getClientIdFromCookie();
                clientIdRef.current = cookieClientId;
                clientIdPromiseRef.current = null;
                resolve(cookieClientId);
              }
            });
            
            console.log("gtag get call made, waiting for callback...");
          } catch (error) {
            console.error("Error calling gtag for client_id:", error);
            const cookieClientId = getClientIdFromCookie();
            clientIdRef.current = cookieClientId;
            clientIdPromiseRef.current = null;
            resolve(cookieClientId);
          }
        } else {
          console.log("No gtag function found, using cookie method for client_id");
          const cookieClientId = getClientIdFromCookie();
          clientIdRef.current = cookieClientId;
          clientIdPromiseRef.current = null;
          resolve(cookieClientId);
        }
      } else {
        console.log("Window not available");
        resolve(null);
      }
    });
    
    clientIdPromiseRef.current = promise;
    return promise;
  };

  const getGA4SessionId = (): Promise<string | null> => {
    // Return cached result if available
    if (sessionIdRef.current !== null) {
      console.log("=== Using cached GA4 session id ===");
      return Promise.resolve(sessionIdRef.current);
    }
    
    // Return existing promise if already in progress
    if (sessionIdPromiseRef.current) {
      console.log("=== GA4 session id request already in progress ===");
      return sessionIdPromiseRef.current;
    }
    
    console.log("=== Getting GA4 session id (new request) ===");
    
    const promise = new Promise<string | null>((resolve) => {
      if (typeof window !== "undefined" && window.gtag) {
        // Try to get session_id from gtag
        console.log("Attempting to get session_id from gtag...");
        
        // Add a timeout to prevent hanging if gtag callback never fires
        const timeoutId = setTimeout(() => {
          console.log("gtag session_id callback timeout - falling back to cookies");
          const cookieSessionId = getSessionIdFromCookie();
          sessionIdRef.current = cookieSessionId;
          sessionIdPromiseRef.current = null;
          resolve(cookieSessionId);
        }, 3000); // 3 second timeout
               
        window.gtag("get", measurementId, "session_id", (sessionId: string) => {
          console.log("Retrieved session_id from gtag:", sessionId);
          clearTimeout(timeoutId); // Clear the timeout since callback fired
          
          if (sessionId) {
            sessionIdRef.current = sessionId;
            sessionIdPromiseRef.current = null; // Clear promise ref
            resolve(sessionId);
          } else {
            console.log("No session_id from gtag, trying cookie fallback");
            const cookieSessionId = getSessionIdFromCookie();
            sessionIdRef.current = cookieSessionId;
            sessionIdPromiseRef.current = null; // Clear promise ref
            resolve(cookieSessionId);
          }
        });
        
        console.log("gtag session_id get call made, waiting for callback...");
      } else {
        console.log("No gtag available, trying cookie fallback for session_id");
        // Fallback to cookie method
        const cookieSessionId = getSessionIdFromCookie();
        sessionIdRef.current = cookieSessionId;
        sessionIdPromiseRef.current = null; // Clear promise ref
        resolve(cookieSessionId);
      }
    });
    
    sessionIdPromiseRef.current = promise;
    return promise;
  };

  

  const sendToMeasurementProtocol = async (clientId: string, sessionId: string | null, eventData: Record<string, any>) => {
    try {
      console.log("=== Sending to Measurement Protocol ===");
      console.log("Client ID:", clientId);
      console.log("Session ID:", sessionId);
      console.log("Event Data:", eventData);
      
      
      // Enhance events with session_id in parameters
      const enhancedEvents = eventData.events?.map((event: any) => ({
        ...event,
        params: {
          ...event.params,
          ...(sessionId && { session_id: sessionId })
        }
      }));
      
      const payload = {
        "client_id": clientId,
        "events": enhancedEvents || eventData.events
        // Include session_id at payload level if available
        // ...(sessionId && { "session_id": sessionId })
      };
      
      console.log("Enhanced events with session_id:", enhancedEvents);
      console.log("Payload being sent:", payload);
      
      const response = await fetch("/api/measurement-protocol", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(`API error: ${errorData.error}`);
      }

      const result = await response.json();
      console.log("Measurement Protocol success:", result);
      

      return result;
    } catch (error) {
      console.error("Measurement Protocol error:", error);
      throw error;
    }
  };

  useEffect(() => {
    const sendMeasurementProtocolData = async () => {
      // Prevent duplicate sends
      // if (sentRef.current) return;
      const timestamp = new Date().toISOString();
      console.log(`=== Measurement Protocol Effect Triggered (${timestamp}) ===`);
      console.log("Data prop:", data);
      console.log("SentRef current:", sentRef.current);
      console.log("measurementId:", measurementId);
      
      // Check if data has actually changed
      const dataChanged = JSON.stringify(data) !== JSON.stringify(lastDataRef.current);
      console.log("Data changed:", dataChanged);
      
      if (!dataChanged && sentRef.current) {
        console.log("Same data already sent - skipping duplicate send");
        return;
      }
      
      // If data changed, reset the sent flag to allow new send
      if (dataChanged) {
        console.log("Data changed - resetting sent flag");
        sentRef.current = false;
      }
      
      // Prevent duplicate sends for the same data
      if (sentRef.current) {
        console.log("Data already sent - skipping duplicate send");
        return;
      }
      
      if (!data || Object.keys(data).length === 0) {
        console.warn("No data provided - cannot send to Measurement Protocol");
        return;
      }
      
      console.log("Proceeding with measurement protocol send...");
     
      console.log("=== Measurement Protocol Effect Triggered ===");
      console.log("Data prop:", data);
      console.log("SentRef current:", sentRef.current);
      
           
      try {
        // Set the flag immediately to prevent race conditions
        sentRef.current = true;
        lastDataRef.current = data;

         // Wait for gtag to be available
        console.log("Ensuring gtag is available...");
        const gtagAvailable = await waitForGtag();
        console.log("gtag available:", gtagAvailable);

        if (!gtagAvailable) {
          console.warn("gtag not available after waiting - falling back to cookie methods");
        }

        console.log("Starting concurrent retrieval of client_id and session_id...");
        
        const [clientId, sessionId] = await Promise.all([
          getGA4ClientId(),
          getGA4SessionId()
        ]);
        
        console.log("Retrieved client_id:", clientId);
        console.log("Retrieved session_id:", sessionId);
        
        if (!clientId) {
          console.warn("No client_id found - cannot send to Measurement Protocol");
          return;
        }
        
        if (!data || Object.keys(data).length === 0) {
          console.warn("No data provided - cannot send to Measurement Protocol");
          return;
        }
        
        console.log("Sending Measurement Protocol data:");
        console.log("- Client ID:", clientId);
        console.log("- Session ID:", sessionId);
        console.log("- Event Data:", data);
        
        await sendToMeasurementProtocol(clientId, sessionId, data);
        sentRef.current = true;
        console.log("Measurement Protocol data sent successfully");
        
      } catch (error) {
        console.error("Error sending Measurement Protocol data:", error);
      }
    };

    sendMeasurementProtocolData();
  }, [data, measurementId]);

  return null;
};

export default MeasurementProtocol;